import { Flash, Plus, Xmark } from 'iconoir-react';
import { ArrowUp } from 'lucide-react';
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import Button from '@superfunnel/ui-core/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/popover';
import { classNames } from '@superfunnel/ui-core/utils';

export type CreditsLevel = 'low' | 'out';

type CreditsTone = CreditsLevel | 'default';

const COMPACT_NUMBER_FORMAT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
});

const RENEW_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
});

const HOVER_CARD_CLOSE_DELAY_MS = 100;
const SALES_CALENDLY_URL = 'https://calendly.com/superfunnelai/sales';

const ACTION_BUTTON_CLASS =
  'gap-1 px-1.5 text-[13px] tracking-[-0.01em] text-text-primary has-[>svg]:px-1.5';

const TONE_CLASSES: Record<
  CreditsTone,
  { header: string; title: string; fill: string }
> = {
  out: {
    header: 'bg-error-500/16',
    title: 'text-error-600',
    fill: 'bg-error-500',
  },
  low: {
    header: 'bg-warning-400/16 dark:bg-warning-500/16',
    title: 'text-warning-700',
    fill: 'bg-warning-500',
  },
  default: {
    header: 'bg-brand-500/10',
    title: 'text-brand-500',
    fill: 'bg-brand-500',
  },
};

export const openSalesCalendly = () => {
  window.open(SALES_CALENDLY_URL, '_blank', 'noopener,noreferrer');
};

export function getCreditsLevel(
  usagePercent: number,
  topUpBalance: number,
): CreditsLevel | null {
  if (usagePercent >= 100) return topUpBalance > 0 ? null : 'out';
  if (usagePercent > 90) return 'low';
  return null;
}

function getLevelPercent(level: CreditsLevel, usagePercent: number) {
  return level === 'out' ? 100 : Math.min(99, Math.floor(usagePercent));
}

function getCardTitle(
  level: CreditsLevel | null,
  usagePercent: number,
  hasNumericLimit: boolean,
) {
  if (level === 'out')
    return `Out of credits – ${getLevelPercent(level, usagePercent)}%`;
  if (level === 'low') {
    return `Low on credits – ${getLevelPercent(level, usagePercent)}%`;
  }
  if (!hasNumericLimit) return 'AI credits';
  return `AI credits – ${Math.floor(usagePercent)}%`;
}

function formatLimit(aiCreditsLimit: number | 'unknown' | 'unlimited') {
  if (aiCreditsLimit === 'unknown') return 'unknown';
  if (aiCreditsLimit === 'unlimited') return 'unlimited';
  return COMPACT_NUMBER_FORMAT.format(aiCreditsLimit).toLowerCase();
}

interface AiCreditsCardProps {
  level: CreditsLevel | null;
  aiCreditsUsed: number;
  aiCreditsLimit: number | 'unknown' | 'unlimited';
  usagePercent: number;
  renewsAt: Date | null;
  onDismiss?: () => void;
  onTopUpClick: () => void;
  onUpgradeClick: () => void;
}

export function AiCreditsCard({
  level,
  aiCreditsUsed,
  aiCreditsLimit,
  usagePercent,
  renewsAt,
  onDismiss,
  onTopUpClick,
  onUpgradeClick,
}: AiCreditsCardProps) {
  const tone = TONE_CLASSES[level ?? 'default'];
  const title = getCardTitle(
    level,
    usagePercent,
    typeof aiCreditsLimit === 'number',
  );

  return (
    <div className="w-full min-w-46 rounded-[calc(var(--radius-xl)+2px)] bg-(--sidebar-page-color) bg-(image:--sidebar-page-image) bg-fixed font-inter">
      <div
        className={classNames(
          'flex flex-col gap-0.5 rounded-[inherit] px-0.5 pt-0.75 pb-0.5 transition-colors duration-slow',
          tone.header,
        )}
      >
        <div
          className={classNames(
            'relative flex h-4 items-center justify-center px-5 text-xs font-medium transition-colors duration-slow',
            tone.title,
          )}
        >
          <p>{title}</p>
          {onDismiss && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss"
              onClick={onDismiss}
              className="absolute top-1/2 right-1 size-4.5 -translate-y-1/2 rounded-full after:absolute after:-inset-2 hover:bg-base-100 dark:hover:bg-base-200"
            >
              <Xmark className="size-4.5 text-base-500" strokeWidth={1.6} />
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-1.5 rounded-xl bg-card px-1.5 pt-2 pb-1.5">
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-1 px-0.5 whitespace-nowrap">
              <p className="flex shrink-0 items-baseline gap-px text-tiny/3.5 text-text-tertiary">
                <span className="text-[11px]/4 font-medium text-text-primary">
                  {aiCreditsUsed.toLocaleString()}
                </span>
                <span>/</span>
                <span>{formatLimit(aiCreditsLimit)}</span>
              </p>
              {renewsAt && (
                <p className="min-w-0 truncate text-tiny/3.5 text-text-tertiary">
                  Renew on: {RENEW_DATE_FORMAT.format(renewsAt)}
                </p>
              )}
            </div>
            <div className="h-1 rounded-full bg-base-200">
              <div
                className={classNames(
                  'h-full rounded-full transition-[width,background-color] duration-slow motion-reduce:transition-none',
                  tone.fill,
                )}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={classNames(ACTION_BUTTON_CLASS, 'flex-1')}
              onClick={onTopUpClick}
            >
              <Plus className="size-4.5" strokeWidth={2} />
              Top up
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={ACTION_BUTTON_CLASS}
              onClick={onUpgradeClick}
            >
              <ArrowUp className="mx-px size-4" strokeWidth={2.25} />
              Upgrade
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AiCreditsBadgeHoverCardProps {
  level: CreditsLevel;
  usagePercent: number;
  side: ComponentProps<typeof PopoverContent>['side'];
  align: ComponentProps<typeof PopoverContent>['align'];
  inline?: boolean;
  className?: string;
  children: ReactNode;
}

export function AiCreditsBadgeHoverCard({
  level,
  usagePercent,
  side,
  align,
  inline = false,
  className,
  children,
}: AiCreditsBadgeHoverCardProps) {
  const tone = TONE_CLASSES[level];
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = () => {
    if (closeTimeoutRef.current === null) return;

    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  };

  const openCard = () => {
    cancelScheduledClose();
    setOpen(true);
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, HOVER_CARD_CLOSE_DELAY_MS);
  };

  useEffect(
    () => () => {
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="AI credits"
          onPointerEnter={openCard}
          onPointerLeave={scheduleClose}
          onClick={(event) => {
            event.preventDefault();
            openCard();
          }}
          className={classNames(
            'flex shrink-0 items-center rounded-lg',
            inline ? 'h-8 gap-1 px-2' : 'w-9 flex-col gap-0.5 py-1.5',
            tone.header,
            tone.title,
            className,
          )}
        >
          <Flash className="size-4" strokeWidth={1.5} />
          <span
            className={classNames(
              'font-semibold tabular-nums',
              inline ? 'text-xs' : 'text-[10px]/3.5',
            )}
          >
            {getLevelPercent(level, usagePercent)}%
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={12}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={openCard}
        onPointerLeave={scheduleClose}
        onClick={(event) => event.stopPropagation()}
        className="w-50 rounded-[calc(var(--radius-xl)+2px)] border-0 bg-transparent p-0"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
